import React from "react";
import { Link } from "@/i18n/router";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Stagger, staggerItem } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { solutions } from "@/data/solutions";
import { useLang } from "@/i18n/LanguageContext";
import { deepLocalize } from "@/i18n/translate";

export default function Solutions() {
  const { lang, tr } = useLang();
  const items = deepLocalize(solutions, lang);
  return (
    <>
      <Seo
        title={tr("Solutions — AI Governance for Critical Sectors")}
        description={tr("Institutional AI governance solutions for Government, Defence, Space, Healthcare, Energy, Finance, Industry and Research.")}
        path="/solutions"
        keywords="AI Governance solutions, Government AI, Defence AI, Critical Infrastructure, Healthcare AI, Energy, Financial Services"
      />

      <PageHero
        overline={tr("Solutions")}
        title={tr("Governance for the sectors that keep Europe running.")}
        subtitle={tr("AstroLife serves organisations where accountability, resilience and compliance are essential — with governance tailored to each strategic domain.")}
        breadcrumbs={[{ label: tr("Solutions") }]}
      />

      <section className="bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((s) => (
              <motion.div key={s.slug} variants={staggerItem}>
                <Link
                  to={`/solutions/${s.slug}`}
                  data-testid={`solutions-index-${s.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-navy-border bg-navy-surface transition-all duration-300 hover:-translate-y-1.5 hover:border-astro-blue/40"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={s.image} alt={s.name} loading="lazy" className="h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-surface via-navy-surface/40 to-transparent" />
                    <div className="absolute left-5 top-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-navy/70 text-astro-light backdrop-blur">
                      <s.icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-xs font-semibold uppercase tracking-wider text-astro-light">{s.tagline}</p>
                    <h2 className="mt-2 font-heading text-xl font-semibold text-white">{s.name}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-silver/60">{s.hero}</p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-astro-light">
                      Explore solution <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <PlatformCta />
    </>
  );
}
